import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  FileText, 
  ClipboardCheck,
  ClipboardList,
  Calendar,
  Download,
  Target,
  Layers,
  CheckCircle2,
  Printer,
  GraduationCap,
  Atom,
  Zap,
  Waves,
  Thermometer,
  Ruler,
  List,
  Sparkles,
  BarChart3,
  PenTool
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { toast } from 'sonner';

import { 
  physicsCurriculum, 
  physicsSchemeOfWork,
  physicsYearlyPlanner
} from '@/data/physicsCurriculum';
import { 
  year11PhysicsUnitTests,
  year12PhysicsUnitTests,
  year13PhysicsUnitTests,
  year11PhysicsExercises,
  year12PhysicsExercises,
  year13PhysicsExercises,
  physicsResources,
  physicsAssessment
} from '@/data/physicsUnitTests';
import { physicsWeeklyLessons } from '@/data/physicsWeeklyLessons';
import AILessonPlanGenerator from '@/components/curriculum/AILessonPlanGenerator';
import FiveELessonGenerator from '@/components/curriculum/FiveELessonGenerator';
import TermExamGenerator from '@/components/curriculum/TermExamGenerator';
import QuickPrintAll from '@/components/curriculum/QuickPrintAll';
import { FileQuestion, Files, Beaker } from 'lucide-react';
import LessonPlanGenerator from '@/components/curriculum/LessonPlanGenerator';
import SubjectSchemeOfWork from '@/components/curriculum/SubjectSchemeOfWork';
import { getCurrentUser } from '@/data/users';
import apiService from '@/services/api';

export default function PhysicsCurriculumPage() {
  const [selectedYear, setSelectedYear] = useState("Year 11");
  const [activeTab, setActiveTab] = useState("curriculum");
  const [selectedTerm, setSelectedTerm] = useState("term1");
  const [teacherYears, setTeacherYears] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  useEffect(() => {
    const fetchTeacherClasses = async () => {
      try {
        const classes = await apiService.getTeacherClasses(true);
        const years = [...new Set(classes
          .filter(c => c.year >= 11 && c.year <= 13 && 
            (c.subject?.toLowerCase().includes('physics') || 
             c.subject?.toLowerCase().includes('phys')))
          .map(c => `Year ${c.year}`)
        )].sort((a, b) => {
          const yearA = parseInt(a.replace('Year ', ''));
          const yearB = parseInt(b.replace('Year ', ''));
          return yearA - yearB;
        });
        
        setTeacherYears(years.length > 0 ? years : ["Year 11", "Year 12", "Year 13"]);
        if (years.length > 0) {
          setSelectedYear(years[0]);
        }
      } catch (error) {
        console.error('Error fetching teacher classes:', error);
        setTeacherYears(["Year 11", "Year 12", "Year 13"]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherClasses();
  }, []);

  const strandIcons = {
    0: Ruler,         // Measurements
    1: Atom,          // Mechanics
    2: Zap,           // Electricity
    3: Waves,         // Light and Waves
    4: Thermometer    // Thermal/Nuclear
  };

  const strandColors = ["blue", "green", "yellow", "purple", "red"];

  const printTest = (test) => {
    const printWindow = window.open('', '_blank');
    let content = `
      <html>
      <head>
        <title>${test.title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 30px; line-height: 1.6; }
          h1 { text-align: center; color: #2563eb; margin-bottom: 5px; }
          .subtitle { text-align: center; color: #666; margin-bottom: 20px; }
          .info-box { background: #f3f4f6; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
          .instructions { margin-bottom: 25px; padding: 10px; border-left: 3px solid #2563eb; background: #eff6ff; }
          .section { margin-bottom: 25px; page-break-inside: avoid; }
          .section-title { background: #2563eb; color: white; padding: 8px 15px; font-weight: bold; margin-bottom: 15px; }
          .question { margin-bottom: 15px; padding: 10px; border-bottom: 1px solid #eee; }
          .q-number { font-weight: bold; color: #2563eb; }
          .options { margin-left: 25px; margin-top: 8px; }
          .option { margin: 5px 0; }
          .marks { float: right; color: #666; font-style: italic; }
          .answer-lines { margin-top: 10px; }
          .answer-line { border-bottom: 1px solid #ccc; height: 25px; margin: 8px 0; }
          @media print { body { padding: 20px; } .section { page-break-inside: avoid; } }
        </style>
      </head>
      <body>
        <h1>${test.title}</h1>
        <p class="subtitle">${selectedYear} Physics - St Patrick's College</p>
        
        <div class="info-box">
          <div class="info-row"><span>Duration:</span><span>${test.duration}</span></div>
          <div class="info-row"><span>Total Marks:</span><span>${test.totalMarks}</span></div>
          <div class="info-row"><span>Name:</span><span>____________________</span></div>
          <div class="info-row"><span>Date:</span><span>____________________</span></div>
        </div>
        
        <div class="instructions">
          <strong>Instructions:</strong>
          <ul>${test.instructions?.map(i => `<li>${i}</li>`).join('') || ''}</ul>
        </div>
        
        ${test.sections?.map(section => `
          <div class="section">
            <div class="section-title">${section.name} (${section.marks} marks)</div>
            ${section.questions?.map(q => `
              <div class="question">
                <span class="q-number">Q${q.id}.</span> ${q.question} <span class="marks">[${q.marks} mark${q.marks > 1 ? 's' : ''}]</span>
                ${q.options ? `<div class="options">${q.options.map(opt => `<div class="option">${opt}</div>`).join('')}</div>` : ''}
                ${!q.options ? `<div class="answer-lines">${Array(Math.ceil(q.marks / 2)).fill('<div class="answer-line"></div>').join('')}</div>` : ''}
              </div>
            `).join('') || ''}
          </div>
        `).join('') || ''}
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  const printSchemeOfWork = () => {
    const printWindow = window.open('', '_blank');
    const termKey = selectedTerm;
    const termData = currentScheme?.[termKey] || [];
    
    let content = `
      <html>
      <head>
        <title>Scheme of Work - ${selectedYear} Physics - ${termKey.replace('term', 'Term ')}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { text-align: center; color: #2563eb; }
          h2 { color: #374151; border-bottom: 2px solid #2563eb; padding-bottom: 5px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background: #2563eb; color: white; }
          tr:nth-child(even) { background: #f8fafc; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h1>St Patrick's College</h1>
        <h2>${selectedYear} Physics - Scheme of Work - ${termKey.replace('term', 'Term ')}</h2>
        <table>
          <thead>
            <tr>
              <th>Week</th>
              <th>Topic</th>
              <th>Learning Outcomes</th>
              <th>Activities</th>
            </tr>
          </thead>
          <tbody>
            ${termData.map(item => `
              <tr>
                <td>${item.week}</td>
                <td>${item.topic}</td>
                <td>${item.outcomes}</td>
                <td>${item.activities}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </body>
      </html>
    `;
    
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  const currentYearData = physicsCurriculum[selectedYear];
  const currentScheme = physicsSchemeOfWork[selectedYear];
  const currentPlanner = physicsYearlyPlanner[selectedYear];

  const getCurrentTests = () => {
    switch(selectedYear) {
      case "Year 11": return year11PhysicsUnitTests;
      case "Year 12": return year12PhysicsUnitTests;
      case "Year 13": return year13PhysicsUnitTests;
      default: return year11PhysicsUnitTests;
    }
  };

  // Get unit tests for year (formatted for TermExamGenerator)
  // Transform from term-based to strand-based format
  const getUnitTestsForYear = () => {
    const tests = getCurrentTests();
    if (!tests) return {};
    
    const strandMap = {};
    
    // Flatten all tests from all terms and group by strand
    ['term1', 'term2', 'term3', 'term4'].forEach(term => {
      const termTests = tests[term];
      if (!Array.isArray(termTests)) return;
      
      termTests.forEach(test => {
        const strandId = test.strand || 'General';
        if (!strandMap[strandId]) {
          strandMap[strandId] = {
            title: test.title || strandId,
            tests: []
          };
        }
        
        // Flatten sections into a single questions array per test
        const allQuestions = [];
        if (test.sections) {
          test.sections.forEach(section => {
            if (section.questions) {
              section.questions.forEach(q => {
                allQuestions.push({
                  ...q,
                  marks: q.marks || 1,
                  section: section.name
                });
              });
            }
          });
        }
        
        if (allQuestions.length > 0) {
          strandMap[strandId].tests.push({
            id: test.id,
            title: test.title,
            questions: allQuestions
          });
        }
      });
    });
    
    return strandMap;
  };

  const getCurrentExercises = () => {
    switch(selectedYear) {
      case "Year 11": return year11PhysicsExercises;
      case "Year 12": return year12PhysicsExercises;
      case "Year 13": return year13PhysicsExercises;
      default: return year11PhysicsExercises;
    }
  };

  return (
    <div className="space-y-6" data-testid="physics-curriculum-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Atom className="h-8 w-8 text-blue-600" />
            Physics Curriculum
          </h1>
          <p className="text-gray-600 mt-1">
            Vanuatu National Physics Syllabus - Years 11, 12 & 13
          </p>
        </div>
        <div className="flex gap-2 items-center">
          <Select 
            value={selectedYear} 
            onValueChange={(value) => {
              setSelectedYear(value);
              setActiveTab("curriculum");
            }}
          >
            <SelectTrigger className="w-32" data-testid="physics-year-selector">
              <SelectValue>{selectedYear}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {(teacherYears.length > 0 ? teacherYears : ["Year 11", "Year 12", "Year 13"]).map(year => (
                <SelectItem key={year} value={year}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Badge variant="outline" className="text-blue-600 border-blue-600">
            5 Strands
          </Badge>
          <Badge variant="outline" className="text-green-600 border-green-600">
            {selectedYear === "Year 11" ? "40% CA / 60% IE" : "40% IA / 60% EE"}
          </Badge>
        </div>
      </div>

      {/* Course Overview Card */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="py-4">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-blue-600">5</p>
              <p className="text-sm text-gray-600">Strands</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-indigo-600">{currentYearData?.strands?.reduce((acc, s) => acc + (s.subStrands?.length || 0), 0) || 12}+</p>
              <p className="text-sm text-gray-600">Sub-strands</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-purple-600">4</p>
              <p className="text-sm text-gray-600">Terms</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-cyan-600">40%</p>
              <p className="text-sm text-gray-600">{selectedYear === "Year 11" ? "Continuous" : "Internal"}</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">60%</p>
              <p className="text-sm text-gray-600">{selectedYear === "Year 11" ? "Internal Exam" : "External Exam"}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 mb-6">
          <TabsTrigger value="curriculum" className="flex items-center gap-2" data-testid="phy-curriculum-tab">
            <BookOpen className="w-4 h-4" />
            <span className="hidden md:inline">Curriculum</span>
          </TabsTrigger>
          <TabsTrigger value="planner" className="flex items-center gap-2" data-testid="phy-planner-tab">
            <Calendar className="w-4 h-4" />
            <span className="hidden md:inline">Planner</span>
          </TabsTrigger>
          <TabsTrigger value="schemes" className="flex items-center gap-2" data-testid="phy-schemes-tab">
            <List className="w-4 h-4" />
            <span className="hidden md:inline">Schemes</span>
          </TabsTrigger>
          <TabsTrigger value="lesson-plans" className="flex items-center gap-2 bg-blue-100" data-testid="phy-lesson-plans-tab">
            <PenTool className="w-4 h-4" />
            <span className="hidden md:inline">Lesson Plans</span>
          </TabsTrigger>
          <TabsTrigger value="term-exam" className="flex items-center gap-2 bg-rose-100" data-testid="phy-term-exam-tab">
            <FileQuestion className="w-4 h-4" />
            <span className="hidden md:inline">Term Exam</span>
          </TabsTrigger>
          <TabsTrigger value="quick-print" className="flex items-center gap-2 bg-cyan-100" data-testid="phy-quick-print-tab">
            <Files className="w-4 h-4" />
            <span className="hidden md:inline">Quick Print</span>
          </TabsTrigger>
          <TabsTrigger value="ai-generator" className="flex items-center gap-2 bg-amber-100" data-testid="phy-ai-generator-tab">
            <Sparkles className="w-4 h-4" />
            <span className="hidden md:inline">AI Generator</span>
          </TabsTrigger>
          <TabsTrigger value="5e-lesson" className="flex items-center gap-2 bg-teal-100" data-testid="phy-5e-lesson-tab">
            <Beaker className="w-4 h-4" />
            <span className="hidden md:inline">5E Lesson</span>
          </TabsTrigger>
          <TabsTrigger value="resources" className="flex items-center gap-2" data-testid="phy-resources-tab">
            <Download className="w-4 h-4" />
            <span className="hidden md:inline">Resources</span>
          </TabsTrigger>
        </TabsList>

        {/* Lesson Plans Tab - Full Week Mon-Fri */}
        <TabsContent value="lesson-plans" data-testid="phy-lesson-plans-content">
          <LessonPlanGenerator 
            defaultYear={parseInt(selectedYear.replace('Year ', ''))} 
            subject="Physics"
            subjectColor="blue"
            allowedYears={['11', '12', '13']}
            weeklyLessons={physicsWeeklyLessons}
          />
        </TabsContent>

        {/* Term Exam Tab */}
        <TabsContent value="term-exam" data-testid="phy-term-exam-content">
          <TermExamGenerator
            subject="Physics"
            year={parseInt(selectedYear.replace('Year ', ''))}
            unitTests={getUnitTestsForYear()}
            subjectColor="blue"
          />
        </TabsContent>

        {/* Quick Print Tab */}
        <TabsContent value="quick-print" data-testid="phy-quick-print-content">
          <QuickPrintAll
            subject="Physics"
            year={parseInt(selectedYear.replace('Year ', ''))}
            weeklyLessons={physicsWeeklyLessons}
            subjectColor="blue"
          />
        </TabsContent>

        {/* AI Generator Tab */}
        <TabsContent value="ai-generator" data-testid="phy-ai-generator-content">
          <AILessonPlanGenerator 
            defaultYear={parseInt(selectedYear.replace('Year ', ''))} 
            defaultSubject="physics" 
            schoolType="secondary"
            allowedYears={teacherYears}
            allowedSubjects={['physics']}
          />
        </TabsContent>

        {/* 5E Model Lesson Plan Tab */}
        <TabsContent value="5e-lesson" data-testid="phy-5e-lesson-content">
          <FiveELessonGenerator
            defaultYear={parseInt(selectedYear.replace('Year ', ''))}
            allowedYears={teacherYears}
          />
        </TabsContent>

        {/* Curriculum Tab */}
        <TabsContent value="curriculum">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-blue-600" />
                  {selectedYear} Physics - Strands & Learning Outcomes
                </CardTitle>
                <CardDescription>
                  Click on each strand to view detailed learning outcomes and topics
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="multiple" className="space-y-3">
                  {currentYearData?.strands?.map((strand, index) => {
                    const IconComponent = strandIcons[index] || Atom;
                    const colorClass = ["blue", "green", "amber", "purple", "red"][index] || "blue";
                    
                    return (
                      <AccordionItem key={strand.id} value={strand.id} className="border rounded-lg">
                        <AccordionTrigger className="px-4 hover:no-underline hover:bg-blue-50">
                          <div className="flex items-center gap-3">
                            <div className={`h-10 w-10 rounded-lg bg-${colorClass}-100 flex items-center justify-center`}>
                              <IconComponent className={`h-5 w-5 text-${colorClass}-600`} />
                            </div>
                            <div className="text-left">
                              <p className="font-semibold">{strand.code}: {strand.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {strand.subStrands?.length || 0} sub-strands
                                {strand.weighting && ` • ${strand.weighting}% weighting`}
                              </p>
                            </div>
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="px-4 pb-4">
                          {strand.majorLearningOutcome && (
                            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <p className="text-sm font-medium text-blue-700">Major Learning Outcome:</p>
                              <p className="text-sm text-gray-700">{strand.majorLearningOutcome}</p>
                            </div>
                          )}
                          
                          {strand.subStrands && strand.subStrands.length > 0 && (
                            <div className="mb-4">
                              <h4 className="font-semibold text-sm text-gray-700 mb-2">Sub-strands:</h4>
                              <Accordion type="multiple" className="space-y-2">
                                {strand.subStrands.map((subStrand) => (
                                  <AccordionItem key={subStrand.id} value={subStrand.id} className="border rounded-lg bg-white">
                                    <AccordionTrigger className="px-3 py-2 hover:no-underline">
                                      <div className="flex items-center gap-2">
                                        <Badge variant="outline" className="text-blue-600 border-blue-300">{subStrand.id}</Badge>
                                        <span className="font-medium text-sm">{subStrand.title}</span>
                                        <Badge className="ml-2 bg-blue-100 text-blue-700 text-xs">
                                          {subStrand.slos?.length || 0} SLOs
                                        </Badge>
                                      </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="px-3 pb-3">
                                      <p className="text-sm text-gray-600 mb-3 italic">{subStrand.description}</p>
                                      <h5 className="font-medium text-xs text-blue-700 mb-2">Student Learning Outcomes:</h5>
                                      <ul className="space-y-1">
                                        {subStrand.slos?.map((slo, sloIdx) => (
                                          <li key={sloIdx} className="flex items-start gap-2 text-sm p-2 bg-blue-50 rounded">
                                            <CheckCircle2 className="h-4 w-4 text-blue-500 shrink-0 mt-0.5" />
                                            <span>{slo}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    </AccordionContent>
                                  </AccordionItem>
                                ))}
                              </Accordion>
                            </div>
                          )}
                          
                          {strand.topics && strand.topics.length > 0 && (
                            <div>
                              <h4 className="font-semibold text-sm text-gray-700 mb-2">Topics:</h4>
                              <div className="grid gap-2">
                                {strand.topics.map((topic, idx) => (
                                  <div key={idx} className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                                    <Badge variant="outline" className="shrink-0">
                                      Week {topic.weeks}
                                    </Badge>
                                    <div>
                                      <p className="font-medium text-sm">{topic.name}</p>
                                      <p className="text-xs text-muted-foreground">{topic.description}</p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Yearly Planner Tab */}
        <TabsContent value="planner" data-testid="phy-planner-content">
          <div className="space-y-6">
            <div className="flex gap-2 flex-wrap">
              {["term1", "term2", "term3", "term4"].map((term) => (
                <Button
                  key={term}
                  variant={selectedTerm === term ? "default" : "outline"}
                  onClick={() => setSelectedTerm(term)}
                  className={selectedTerm === term ? "bg-blue-600 hover:bg-blue-700" : ""}
                >
                  Term {term.replace("term", "")}
                </Button>
              ))}
            </div>

            {currentPlanner?.[selectedTerm] && (
              <Card>
                <CardHeader className="bg-blue-50 border-b border-blue-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-blue-800">
                        Term {selectedTerm.replace("term", "")} Overview
                      </CardTitle>
                      <CardDescription className="text-blue-700">
                        {currentPlanner[selectedTerm].weeks} • {currentPlanner[selectedTerm].focus}
                      </CardDescription>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700">
                      {currentPlanner[selectedTerm].assessment}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="space-y-4">
                    {currentPlanner[selectedTerm].units?.map((unit, idx) => (
                      <div key={idx} className="p-4 border rounded-lg hover:border-blue-300 transition-colors">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-blue-800">{unit.title}</h4>
                          <Badge variant="outline" className="text-blue-600">{unit.weeks}</Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          <span className="font-medium">{unit.strand}</span> - Sub-strand {unit.subStrand}
                        </p>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {unit.topics?.map((topic, tIdx) => (
                            <Badge key={tIdx} variant="outline" className="text-xs bg-blue-50">
                              {topic}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Strand Weightings - {selectedYear}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {currentYearData?.strands?.map((strand) => (
                    <div key={strand.id} className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="font-medium">{strand.code}: {strand.name}</span>
                        <span className="text-gray-600">{strand.weighting}%</span>
                      </div>
                      <Progress value={strand.weighting * 2} className="h-2" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Schemes of Work Tab */}
        <TabsContent value="schemes" data-testid="phy-schemes-content">
          <SubjectSchemeOfWork
            subject="Physics"
            year={parseInt(selectedYear.replace('Year ', ''))}
            weeklyLessons={physicsWeeklyLessons}
            subjectColor="blue"
          />
        </TabsContent>

        {/* Resources Tab */}
        <TabsContent value="resources" data-testid="phy-resources-content">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="w-5 h-5" />
                  Teaching Resources
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(physicsResources || []).map((resource, idx) => (
                    <div key={idx} className="p-4 border rounded-lg hover:border-blue-300 hover:shadow-sm transition-all">
                      <div className="flex items-start justify-between mb-2">
                        <h4 className="font-medium text-blue-800">{resource.title}</h4>
                        <Badge variant="outline" className="text-blue-600 text-xs">{resource.type}</Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{resource.description}</p>
                      <div className="flex items-center justify-between">
                        <div className="flex gap-1">
                          <Badge className="bg-gray-100 text-gray-600 text-xs">{resource.unit}</Badge>
                          {resource.year && <Badge className="bg-blue-50 text-blue-600 text-xs">{resource.year}</Badge>}
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="outline" className="text-blue-600">
                            <Download className="w-4 h-4 mr-1" />
                            Download
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="text-amber-600 hover:bg-amber-50"
                            onClick={() => {
                              setActiveTab('ai-generator');
                              toast.info(`Generate custom materials for: ${resource.title}`);
                            }}
                          >
                            <Sparkles className="w-4 h-4 mr-1" />
                            AI Generate
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5" />
                  Assessment Structure - {selectedYear}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                    <h4 className="font-medium text-green-800 mb-3 flex items-center gap-2">
                      <ClipboardList className="w-4 h-4" />
                      {selectedYear === "Year 11" ? "Continuous Assessment (40%)" : "Internal Assessment (40%)"}
                    </h4>
                    <div className="space-y-2">
                      {selectedYear === "Year 11" ? (
                        physicsAssessment?.year11?.continuousAssessment?.components?.map((comp, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span>{comp.name}</span>
                            <Badge variant="outline">{comp.weight}%</Badge>
                          </div>
                        ))
                      ) : (
                        physicsAssessment?.[selectedYear.toLowerCase().replace(' ', '')]?.internalAssessment?.components?.map((comp, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span>{comp.name}</span>
                            <Badge variant="outline">{comp.weight}%</Badge>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                    <h4 className="font-medium text-purple-800 mb-3 flex items-center gap-2">
                      <ClipboardCheck className="w-4 h-4" />
                      {selectedYear === "Year 11" ? "Internal Examination (60%)" : "External Examination (60%)"}
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="mt-3">
                        <p className="font-medium mb-2">Strand Weights:</p>
                        <div className="space-y-1">
                          {currentYearData?.strands?.map((strand) => (
                            <div key={strand.id} className="flex justify-between">
                              <span>{strand.code}: {strand.name.split(' ')[0]}</span>
                              <Badge>{strand.weighting}%</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
